import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'components/ui/button'
import useToast from 'hooks/useToast'
import { updatePartner } from 'submission/lib/partner-api'
import type { Partner, PartnerStatus, PartnerWriteBody, Tier } from './types'
import { HANDLE_RE, TIERS } from './types'

type Props = {
  partner: Partner
  onSaved: (p: Partner) => void
}

const inputCls =
  'w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary'

const PartnerProfileForm: React.FC<Props> = ({ partner, onSaved }) => {
  const { showToast } = useToast()
  const [handle, setHandle] = useState(partner.handle)
  const [status, setStatus] = useState<PartnerStatus>(partner.status)
  const [tierOverride, setTierOverride] = useState<Tier | ''>(
    partner.tier_override ?? '',
  )
  const [payoutWallet, setPayoutWallet] = useState(partner.payout_wallet ?? '')
  const [payoutChainId, setPayoutChainId] = useState(
    partner.payout_chain_id == null ? '' : String(partner.payout_chain_id),
  )
  const [notes, setNotes] = useState(partner.notes ?? '')
  const [saving, setSaving] = useState(false)

  // Whether the form had unsaved edits as of the END of the previous render
  // (see the tracking effect below) — read by the resync effect so it can
  // stay [partner]-only (exhaustive-deps clean) while still skipping the
  // resync when the admin has unsaved edits. This must reflect the *prior*
  // render's dirtiness, not the current one: the current render's `dirty`
  // is computed against whatever `partner` just changed to, which is
  // exactly the value a legitimate resync needs to apply, so gating on it
  // directly would permanently block resyncs.
  const dirtyRef = useRef(false)

  useEffect(() => {
    // A background revalidation (SWR refetch on focus/reconnect, or another
    // admin's change) must not clobber an in-progress edit. Skip the resync
    // while dirty; it re-applies once the admin saves (dirty becomes false
    // and `partner` updates via onSaved -> mutate()) or discards their edits.
    if (dirtyRef.current) return
    setHandle(partner.handle)
    setStatus(partner.status)
    setTierOverride(partner.tier_override ?? '')
    setPayoutWallet(partner.payout_wallet ?? '')
    setPayoutChainId(
      partner.payout_chain_id == null ? '' : String(partner.payout_chain_id),
    )
    setNotes(partner.notes ?? '')
  }, [partner])

  // Only changed fields are sent: the backend preserves omitted ones.
  const diff = (): PartnerWriteBody => {
    const body: PartnerWriteBody = {}
    if (handle.trim() !== partner.handle) body.handle = handle.trim()
    if (status !== partner.status) body.status = status
    if (tierOverride !== (partner.tier_override ?? ''))
      body.tier_override = tierOverride
    if (
      payoutWallet.trim() !== (partner.payout_wallet ?? '') &&
      payoutWallet.trim()
    )
      body.payout_wallet = payoutWallet.trim()
    if (
      payoutChainId !==
        (partner.payout_chain_id == null
          ? ''
          : String(partner.payout_chain_id)) &&
      payoutChainId
    )
      body.payout_chain_id = Number(payoutChainId)
    if (notes !== (partner.notes ?? '')) body.notes = notes
    return body
  }

  const body = diff()
  const dirty = Object.keys(body).length > 0

  // Runs after every render (deliberately no dependency array), so it
  // records this render's dirtiness for the *next* commit — after the
  // resync effect above has already acted on the previous value.
  useEffect(() => {
    dirtyRef.current = dirty
  })

  const handleValid = HANDLE_RE.test(handle.trim())

  const save = async () => {
    if (!dirty || !handleValid) return
    setSaving(true)
    try {
      const updated = await updatePartner(partner.id, body)
      showToast({ type: 'success', message: 'Partner updated' })
      onSaved(updated)
    } catch (e) {
      showToast({ type: 'error', message: (e as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="border border-neutral-200 rounded-lg p-4 space-y-3">
      <h2 className="font-semibold">Profile</h2>
      <div className="text-xs text-neutral-500">
        Privy ID {partner.privy_id} · Code {partner.code} · Computed tier{' '}
        {partner.tier}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block text-sm">
          Handle (3–32 letters, digits, _ or -)
          <input
            className={inputCls}
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          Status
          <select
            className={inputCls}
            value={status}
            onChange={(e) => setStatus(e.target.value as PartnerStatus)}
          >
            <option value="active">active</option>
            <option value="suspended">suspended</option>
          </select>
        </label>
        <label className="block text-sm">
          Tier override
          <select
            className={inputCls}
            value={tierOverride}
            onChange={(e) => setTierOverride(e.target.value as Tier | '')}
          >
            <option value="">None (computed)</option>
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          Payout wallet
          <input
            className={inputCls}
            value={payoutWallet}
            onChange={(e) => setPayoutWallet(e.target.value)}
            placeholder="0x…"
          />
          <span className="text-xs text-neutral-500">
            Cannot be cleared once set; enter a new address to replace it.
          </span>
        </label>
        <label className="block text-sm">
          Payout chain ID
          <input
            className={inputCls}
            type="number"
            value={payoutChainId}
            onChange={(e) => setPayoutChainId(e.target.value)}
          />
        </label>
        <label className="block text-sm md:col-span-2">
          Notes
          <textarea
            className={inputCls}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={!dirty || !handleValid || saving}>
          Save profile
        </Button>
      </div>
    </div>
  )
}

export default PartnerProfileForm
